import React, { useEffect, useMemo, useState } from "react";
import EmailTemplatesHeader from "./EmailTemplatesHeader";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Popover,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../utils/dataTableShell";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { AxiosResponse } from "axios";
import { debounce } from "lodash";
import { v4 as uuid } from "uuid";
import { CloseRounded } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";

interface EmailTemplate {
  id: string;
  name: string;
  providerTemplateId: string;
  dynamicVariables: string[];
}

const EmailTemplatesList = () => {
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState<string>(uuid());
  const [loading, setLoading] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [data, setData] = useState<EmailTemplate[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [varPopover, setVarPopover] = useState<{
    anchorEl: HTMLElement | null;
    variables: string[];
  }>({ anchorEl: null, variables: [] });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        let url = `/provider-email-templates?provider=sendgrid&page=${
          page + 1
        }&size=${pageSize}`;
        if (searchText) {
          url += `&search=${searchText}`;
        }
        const res: AxiosResponse = await http.get(url);
        const templateList = res.data?.data?.data?.map((item: any) => ({
          id: item?.id || "",
          providerTemplateId: item?.providerTemplateId || "",
          name: item?.name || "Untitled Template",
          dynamicVariables: item?.activeVersionContent?.dynamicVariables ?? [],
        }));
        setData(templateList || []);
        setDataCount(res.data?.data?.meta?.paginationOptions?.totalItems ?? 0);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [toggleLoader, searchText, page]);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    []
  );

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
    setSearchId(uuid());
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res: AxiosResponse = await http.post(
        `/provider-email-templates/sync/sendgrid`
      );
      toastMessage(
        "success",
        res.data?.message || "Templates synced successfully"
      );
      setToggleLoader((prev) => !prev);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <EmailTemplatesHeader onSync={handleSync} syncing={syncing} />
      <Box sx={{ p: 2.5, ...dataTablePageShell("calc(100% - 72px)") }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            toolbar={
              <TextField
                key={searchId}
                placeholder="Search for Email Templates"
                style={{ width: "30%" }}
                defaultValue={searchText}
                onChange={(e) => modifySearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <CloseRounded />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            }
            footer={
              <DataTableFooter
                filteredCount={dataCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                loading={loading}
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Template Name</StyledTableCell>
                  <StyledTableCell>Provider Template ID</StyledTableCell>
                </TableRow>
              </TableHead>
              {!loading && data?.length > 0 && (
                <TableBody>
                  {data?.map((row: EmailTemplate) => (
                    <TableRow key={row?.id}>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {row?.name || "-"}
                          {row?.dynamicVariables?.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                setVarPopover({
                                  anchorEl: e.currentTarget,
                                  variables: row.dynamicVariables,
                                })
                              }
                              sx={{ color: "text.secondary", p: 0.25 }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        {row?.providerTemplateId || "-"}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            <Popover
              open={Boolean(varPopover.anchorEl)}
              anchorEl={varPopover.anchorEl}
              onClose={() => setVarPopover({ anchorEl: null, variables: [] })}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Available variables — click to copy
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {varPopover.variables.map((v) => (
                    <Chip
                      key={v}
                      label={`{{${v}}}`}
                      size="small"
                      onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}
                      sx={{ fontFamily: "monospace", cursor: "pointer" }}
                    />
                  ))}
                </Box>
              </Box>
            </Popover>
            {!loading && data.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </DataTableLayout>
        </Box>
      </Box>
    </>
  );
};

export default EmailTemplatesList;
